import type { GridApi, IRowNode } from 'ag-grid-community';
import { getCoarsestIncrement, type PrecisionCandidate } from '../../utils/getCoarsestIncrement';
import type { SiblingLeafPrecision } from '../BlotterTable/columns/size.types';

export function getImmediateParent(node: IRowNode): IRowNode | undefined {
  return node.group ? node : (node.parent ?? undefined);
}

function matchesParent(parent: IRowNode, options: SiblingLeafPrecision): boolean {
  return (parent.key ?? '').startsWith(options.parentKeyPrefix);
}

/**
 * Collects values from every leaf under the immediate parent of `node`.
 * Only parents whose key starts with `parentKeyPrefix` qualify.
 * Returns undefined for group rows and parentless leaves so callers keep their own increment.
 */
export function getSiblingLeafCandidates<T>(
  node: IRowNode | null | undefined,
  getCandidate: (leaf: IRowNode) => T,
  options: SiblingLeafPrecision
): T[] | undefined {
  if (node == null || node.group || node.parent == null) {
    return undefined;
  }
  const parent = getImmediateParent(node);
  if (parent == null || !matchesParent(parent, options)) {
    return undefined;
  }
  const leaves = parent.allLeafChildren ?? [];
  if (leaves.length <= 1) {
    return undefined;
  }
  return leaves.map(getCandidate);
}

export function getAlignedSiblingIncrement(
  node: IRowNode | null | undefined,
  getCandidate: (leaf: IRowNode) => PrecisionCandidate,
  options: SiblingLeafPrecision
): string | undefined {
  const candidates = getSiblingLeafCandidates(node, getCandidate, options);
  if (candidates == null) {
    return undefined;
  }
  return getCoarsestIncrement(candidates);
}

export type SiblingPrecisionColumnConfig = SiblingLeafPrecision & {
  colId: string;
};

/**
 * After an incremental row transaction, force-refresh sibling leaves so they recompute
 * shared increment. Cell values on those rows may be unchanged, so AG Grid would otherwise skip them.
 */
export function refreshSiblingPrecisionCells(
  api: Pick<GridApi, 'refreshCells'>,
  touchedNodes: Array<IRowNode | null | undefined>,
  columnConfigs: SiblingPrecisionColumnConfig[]
): void {
  if (columnConfigs.length === 0) {
    return;
  }

  const rowNodes = new Set<IRowNode>();
  const colIds = new Set<string>();

  for (const node of touchedNodes) {
    if (node == null) {
      continue;
    }
    for (const config of columnConfigs) {
      const parent = getImmediateParent(node);
      if (parent == null || !matchesParent(parent, config)) {
        continue;
      }

      const leaves = parent.allLeafChildren ?? [];
      if (leaves.length === 0) {
        continue;
      }

      for (const leaf of leaves) {
        rowNodes.add(leaf);
      }
      colIds.add(config.colId);
    }
  }

  if (rowNodes.size === 0 || colIds.size === 0) {
    return;
  }

  api.refreshCells({
    rowNodes: [...rowNodes],
    columns: [...colIds],
    force: true,
    suppressFlash: true,
  });
}
